/**
 * Assets Table Configuration
 *
 * Column widths migrated to central registry (Day 3 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Default settings and table-specific constants.
 */

import type { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import { generateStorageKey } from '@/utils/tableConfig.util';

/**
 * Default column order for assets table
 *
 * Ordering convention:
 * 1. Status - Always first when present (user can remove or reorder)
 * 2. Core columns - Identifier, Tags, Origin
 * 3. Date columns - First Seen, Last Seen always at end when present (user can remove or reorder)
 *
 * Note: This is the DEFAULT order. Users have full control to reorder or remove any column
 * except 'identifier' which is required.
 */
export const ASSET_DEFAULT_COLUMN_ORDER = [
  'status', // immutable lead 1
  'severity', // immutable lead 2 (NEW — maps to Asset.riskSeverity)
  'riskScore', // immutable lead 3 (NEW — maps to Asset.riskScore)
  'identifier', // immutable lead 4 (required)
  'type', // Asset kind (Application / IP / Domain / Web Application / Other)
  // 'application' is intentionally NOT a default column. It remains fully
  // selectable via the column picker; it was just removed from the default set.
  'origins', // Source: Shown by default
  'created', // Convention: Last by default
  'visited', // Convention: Last by default
] as const;

// ============================================================================
// COLUMN GROUPING & BEHAVIOR
// ============================================================================

/**
 * Columns that support sorting
 * Columns not in this list will have sorting disabled
 *
 * IMPORTANT: These column IDs MUST match the backend's AllowedColumns map
 * in modules/chariot/backend/build/pkg/query/allowed_columns.go
 *
 * Backend validation will reject queries with invalid orderBy fields.
 */
export const SORTABLE_COLUMNS = [
  'status',
  'identifier',
  'riskScore',
  'asname',
  'asnumber',
  'attackSurface',
  'class',
  'country',
  'origins',
  'port',
  'protocol',
  'tags',
  'profile',
  'created',
  'visited',
] as const;

/**
 * Maps frontend column IDs to backend Neo4j property names when they differ.
 * Most columns use the same name in both; only add entries here for mismatches.
 */
export const SORT_KEY_TO_BACKEND: Record<string, string> = {
  riskScore: 'risk_score',
};

/** The four leading columns that are always present, in fixed order, and non-removable. */
export const ASSET_IMMUTABLE_COLUMN_IDS = [
  'status',
  'severity',
  'riskScore',
  'identifier',
] as const;

/**
 * Column ID that must always be visible (cannot be removed)
 *
 * @deprecated prefer ASSET_IMMUTABLE_COLUMN_IDS; retained for existing importers.
 */
export const REQUIRED_COLUMN_ID = 'identifier' as const;

/**
 * Reconcile a user's persisted active-column list with the current immutable
 * leading block.
 * - undefined (user never customized) → return the default active ids as-is.
 * - otherwise: strip any immutable ids from the stored list, then prepend the
 *   immutable block in fixed order, preserving the user's order for the rest.
 *   Non-immutable saved columns are preserved.
 */
export const reconcileAssetColumns = (
  stored: string[] | undefined,
  config: SortableColumnsConfig
): string[] => {
  if (stored === undefined) {
    return config.active.map(c => c.id);
  }
  const immutable = ASSET_IMMUTABLE_COLUMN_IDS as readonly string[];
  const rest = stored.filter(id => !immutable.includes(id));
  return [...immutable, ...rest];
};

// ============================================================================
// TABLE CONFIGURATION
// ============================================================================

/**
 * Storage key for persisting column configuration
 * v4: Centralized width config with proper type safety
 */
export const ASSET_TABLE_STORAGE_KEY = generateStorageKey(
  'assets',
  undefined,
  7
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a column supports sorting
 *
 * @param columnId - The column identifier to check
 * @returns True if the column is in the sortable columns list
 */
export const isColumnSortable = (columnId: string): boolean => {
  return SORTABLE_COLUMNS.includes(
    columnId as (typeof SORTABLE_COLUMNS)[number]
  );
};
